import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";

import { useAuth } from "context/AuthContext";
import { useCart } from "context/CartContext";
import { navigateAccountPortal } from "lib/staffFrontend";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import shortLogo from "assets/images/logos/ShortLogo.png";
import {
  VbsHeaderAccountIcon,
  VbsHeaderCartIcon,
  VbsHeaderCatalogIcon,
  VbsHeaderQrScanIcon,
} from "./VbsHeaderIcons";
import { vbs } from "../lib/theme";

function HeaderTap({ onClick, label, children }) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={label}
      sx={{
        p: 0.35,
        minWidth: 40,
        minHeight: 40,
        display: "grid",
        placeItems: "center",
        borderRadius: "8px",
        lineHeight: 0,
        transition: "opacity 0.15s ease",
        "&:active": { opacity: 0.65 },
      }}
    >
      {children}
    </ButtonBase>
  );
}

HeaderTap.propTypes = {
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default function VbsShell({
  children,
  showLogo = false,
  bleed = false,
  rightAction,
  onBack,
  title,
  footer,
}) {
  const navigate = useNavigate();
  const { token, pluginRole } = useAuth();
  const { itemCount } = useCart();
  const { t } = useStorefrontCopy();
  const heroHeader = !!bleed;

  const defaultRight = (
    <Stack direction="row" spacing={0.15} alignItems="center">
      <HeaderTap label={t("common.catalog", "Catalog")} onClick={() => navigate("/m/catalog")}>
        <VbsHeaderCatalogIcon hero={heroHeader} />
      </HeaderTap>
      <HeaderTap
        label={t("registeredUnits.scanTitle", "Scan")}
        onClick={() => navigate("/m/scan")}
      >
        <VbsHeaderQrScanIcon hero={heroHeader} />
      </HeaderTap>
      {token ? (
        <>
          <HeaderTap
            label={t("common.account", "Account")}
            onClick={() => navigateAccountPortal({ token, role: pluginRole, navigate })}
          >
            <VbsHeaderAccountIcon hero={heroHeader} />
          </HeaderTap>
          <HeaderTap label={t("common.cart", "Cart")} onClick={() => navigate("/m/cart")}>
            <Badge
              badgeContent={itemCount || 0}
              color="error"
              invisible={!itemCount}
              sx={{ "& .MuiBadge-badge": { fontWeight: 700, fontSize: 10, minWidth: 16, height: 16 } }}
            >
              <VbsHeaderCartIcon hero={heroHeader} />
            </Badge>
          </HeaderTap>
        </>
      ) : (
        <ButtonBase
          onClick={() => navigate("/m/authentication/sign-in")}
          sx={{
            px: 0.75,
            py: 0.5,
            minHeight: 36,
            color: heroHeader ? "rgba(255,255,255,0.94)" : vbs.ink,
            fontSize: 14,
            fontWeight: 650,
            letterSpacing: 0.02,
            borderRadius: "8px",
            border: heroHeader ? "1px solid rgba(255,255,255,0.35)" : `1px solid ${vbs.hairline}`,
          }}
        >
          {t("common.signIn", "Sign in")}
        </ButtonBase>
      )}
    </Stack>
  );

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: heroHeader ? vbs.ink : vbs.canvas, color: vbs.ink }}>
      <Box
        sx={{
          position: bleed ? "absolute" : "sticky",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 30,
          bgcolor: heroHeader
            ? "linear-gradient(180deg, rgba(15,15,18,0.75) 0%, rgba(15,15,18,0.28) 65%, transparent 100%)"
            : "#FFFFFF",
          backdropFilter: heroHeader ? "none" : "blur(12px)",
          borderBottom: heroHeader ? "none" : `1px solid ${vbs.hairline}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: { xs: 2, sm: 3 },
            maxWidth: vbs.tabletMax,
            mx: "auto",
            width: "100%",
            pt: "max(10px, env(safe-area-inset-top))",
            pb: 1,
            minHeight: 52,
          }}
        >
          <Box
            sx={{
              flex: "1 1 0",
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 0.5,
            }}
          >
            {onBack ? (
              <ButtonBase
                onClick={onBack}
                aria-label={t("common.back", "Back")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.35,
                  p: 0.35,
                  minHeight: 40,
                  color: vbs.ink,
                  borderRadius: "8px",
                  "&:active": { opacity: 0.65 },
                }}
              >
                <Box
                  component="svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  sx={{ width: 20, height: 20 }}
                >
                  <path
                    d="M15 6 9 12l6 6"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </Box>
              </ButtonBase>
            ) : null}
            {showLogo ? (
              <Box
                component="img"
                src={shortLogo}
                alt="Valve Body Specialties"
                onClick={() => navigate("/m")}
                sx={{
                  height: { xs: 40, sm: 44 },
                  width: "auto",
                  maxWidth: { xs: 140, sm: 168 },
                  cursor: "pointer",
                  objectFit: "contain",
                  objectPosition: "left center",
                  display: "block",
                }}
              />
            ) : null}
          </Box>

          {title && !bleed ? (
            <Box
              sx={{
                flex: "0 1 auto",
                minWidth: 0,
                px: 0.5,
                fontSize: 17,
                fontWeight: 800,
                letterSpacing: -0.3,
                lineHeight: 1.15,
                color: vbs.ink,
                textAlign: "center",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {title}
            </Box>
          ) : null}

          <Box
            sx={{
              flex: "1 1 0",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Box sx={{ flexShrink: 0 }}>{rightAction || defaultRight}</Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: bleed || showLogo ? 0 : 0, pt: 0, pb: footer ? 18 : 10, color: vbs.ink }}>
        {children}
      </Box>

      {footer ? (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: "calc(70px + env(safe-area-inset-bottom))",
            zIndex: 1400,
            px: 2,
            py: 1.25,
            bgcolor: "rgba(255,255,255,0.96)",
            backdropFilter: "blur(16px)",
            borderTop: `1px solid ${vbs.hairline}`,
            boxShadow: "0 -10px 28px rgba(15,15,18,0.06)",
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Box>
  );
}

VbsShell.propTypes = {
  children: PropTypes.node.isRequired,
  showLogo: PropTypes.bool,
  bleed: PropTypes.bool,
  rightAction: PropTypes.node,
  onBack: PropTypes.func,
  title: PropTypes.string,
  footer: PropTypes.node,
};

VbsShell.defaultProps = {
  showLogo: false,
  bleed: false,
  rightAction: null,
  onBack: null,
  title: "",
  footer: null,
};
